import React, { useState } from 'react';
import { Modal, Form, Input, Button, Typography, Space, QRCode, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Text, Title } = Typography;
const { OTP } = Input;

const TOTPActivateModal = ({ visible, onCancel, userEmail, onSuccess }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [totpData, setTotpData] = useState(null);
    
    // Get user token from localStorage
    const getUserToken = () => {
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        return userInfo.token;
    };

    // Auto-setup TOTP when modal opens
    React.useEffect(() => {
        if (visible && userEmail && !totpData) {
            handleSetup();
        }
    }, [visible, userEmail]);

    const handleSetup = async () => {
        setLoading(true);
        try {
            const token = getUserToken();
            if (!token) {
                message.error('Authentication required');
                setLoading(false);
                return;
            }
            
            const response = await axios.post('/dotnet_api/v1/auth/setup-totp', {
                email: userEmail
            }, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            setTotpData(response.data);
        } catch (error) {
            message.error('Failed to setup TOTP');
        }
        setLoading(false);
    };

    const handleVerify = async (values) => {
        setLoading(true);
        try {
            const token = getUserToken();
            if (!token) {
                message.error('Authentication required');
                setLoading(false);
                return;
            }
            
            await axios.post('/dotnet_api/v1/auth/verify-totp', {
                email: userEmail,
                code: values.code
            }, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            onSuccess();
            onCancel();
        } catch (error) {
            message.error('Invalid code');
        }
        setLoading(false);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        message.success('Copied to clipboard');
    };

    const handleCancel = () => {
        setTotpData(null);
        form.resetFields();
        onCancel();
    };

    return (
        <Modal
            title="Setup Two-Factor Authentication"
            open={visible}
            onCancel={handleCancel}
            footer={null}
            width={500}
        >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {loading && !totpData ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Text>Setting up TOTP...</Text>
                    </div>
                ) : totpData ? (
                    <>
                        <div style={{ textAlign: 'center' }}>
                            <Title level={4}>Scan QR Code</Title>
                            <Text type="secondary">
                                Open your authenticator app and scan this QR code
                            </Text>
                        </div>

                        <div style={{ 
                            textAlign: 'center', 
                            padding: '20px 0',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}>
                            <QRCode value={totpData.qrCodeUrl} size={200} />
                        </div>

                        <div>
                            <Text strong>Manual Entry Key:</Text>
                            <Space.Compact style={{ width: '100%', marginTop: 8 }}>
                                <Input
                                    value={totpData.manualEntryKey}
                                    readOnly
                                    style={{ fontFamily: 'monospace' }}
                                />
                                <Button
                                    icon={<CopyOutlined />}
                                    onClick={() => copyToClipboard(totpData.manualEntryKey)}
                                >
                                    Copy
                                </Button>
                            </Space.Compact>
                        </div>

                        <Form form={form} onFinish={handleVerify} layout="vertical">
                            <Form.Item
                                name="code"
                                label="Enter 6-digit code from your app"
                                rules={[{ required: true, message: 'Please enter the code' }]}
                                style={{ display: 'flex', justifyContent: 'center' }}
                            >
                                <OTP
                                    size="large"
                                    length={6}
                                    placeholder="Enter code"
                                />
                            </Form.Item>
                            
                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading}
                                    block
                                    size="large"
                                >
                                    Verify & Enable TOTP
                                </Button>
                            </Form.Item>
                        </Form>
                    </>
                ) : (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Text type="danger">Failed to setup TOTP. Please try again.</Text>
                    </div>
                )}
            </Space>
        </Modal>
    );
};

export default TOTPActivateModal;
